import { useEffect, useState, memo, useRef } from "react";
import { useParams } from "react-router-dom";

import { ThreadsProvider } from "@dev-streetbeat/chat";
import classNames from "classnames";

import appStatusStore from "shared/store/appStatusStore";
import { ChatHistoryPanel } from "./panels/chat-history/ChatHistoryPanel";

import { AttachmentsPanel } from "./panels/attachments/AttachmentsPanel";
import useAppStatus from "shared/store/appStatusStore";

import { appConfig } from "config/app-config";
import { useTabletAndBelowMediaQuery } from "shared/store/responsiveStore";
import colorsStore from "shared/store/colorsStore";
import { useChatConfig } from "shared/hooks/useChatConfig";

import styles from "./ActivityPanelContainer.module.scss";

const sizeExpanded = appConfig.activityPanel.panelWidthSizeExpanded;

const ActivityPanelContainerBase = () => {
  const params = useParams();
  const threadId = params.threadId;
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const isTabletOrBelow = useTabletAndBelowMediaQuery();

  const appStatus = appStatusStore((state) => state.appStatus);


  const setAppStatus = useAppStatus((state) => state.setAppStatus);

  const [expanded, setExpanded] = useState(false);
  const [widthSize, setWidthSize] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);


  useEffect(() => {
    if (threadId) {
      if (appStatus !== "history") {
        setAppStatus("idle");
      }
    }
  }, [threadId]);

  useEffect(() => {
    switch (appStatus) {
      case "history":
      case "attachments":
        setIsClosing(false);
        setExpanded(true);
        setWidthSize(sizeExpanded);
        break;
      default:
        if (expanded && isTabletOrBelow) {
          // For tablet, start closing animation before setting expanded to false
          setIsClosing(true);
          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          // Set timeout to actually hide after animation completes
          timeoutRef.current = setTimeout(() => {
            setExpanded(false);
            setIsClosing(false);
            setWidthSize(0);
          }, 300); // Match animation duration
        } else {
          setExpanded(false);
          setWidthSize(0);
        }
    }
  }, [appStatus, isTabletOrBelow, expanded]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    setAppStatus("idle");
  };

  const { chatConfig } = useChatConfig();

  // For desktop view, use traditional aside with controlled width
  if (!isTabletOrBelow) {
    return (
      <aside
        className="h-full overflow-hidden duration-500"
        style={{ width: widthSize }}
      >
        <div className="lex-col flex h-full grid-cols-2 justify-stretch gap-x-4 gap-y-4">
          {appStatus === "history" && (
            <ThreadsProvider config={chatConfig}>
              <ChatHistoryPanel
                expanded={expanded}
                onClose={handleClose}
                sizeExpanded={sizeExpanded}
              />
            </ThreadsProvider>
          )}
          {appStatus === "attachments" && (
            <AttachmentsPanel expanded={expanded} onClose={handleClose} />
          )}
        </div>
      </aside>
    );
  }

  // For tablet and mobile, show as a fixed overlay when expanded
  if (isTabletOrBelow && (expanded || isClosing)) {
    return (
      <div
        className={classNames(styles.activityPanelContainer, {
          [styles.closing]: isClosing,
          [styles.expanded]: expanded,
        })}
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: colorsConfig.white,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          borderLeft: `1px solid ${colorsConfig.grayLight}`,
        }}
      >
        {appStatus === "history" && (
          <ThreadsProvider config={chatConfig}>
            <ChatHistoryPanel
              expanded={expanded}
              onClose={handleClose}
              sizeExpanded={sizeExpanded}
            />
          </ThreadsProvider>
        )}
        {appStatus === "attachments" && (
          <AttachmentsPanel expanded={expanded} onClose={handleClose} />
        )}
      </div>
    );
  }

  // Return empty fragment when not expanded on tablet/mobile
  return <></>;
};

// The component will only re-render when its props change
export const ActivityPanelContainer = memo(ActivityPanelContainerBase);
